<template>
  <div>
    <v-row row wrap>
      <v-col v-if="!isHotspots" class="col-12 px-0 pt-0">
        <div class="GaugeHolderSvg GaugeHolderSvgHorizontal">
          <div class="text-left" v-if="showTitle">
            <span
              class="details-heading application-heading"
              :class="{ 'details-heading-dark ml-3': dark }"
              >{{ labels.pulse }}</span
            ><PulseAndRiskDefinitions
              :initial="'STABILITY'"
            ></PulseAndRiskDefinitions>
          </div>
          <StabilityGaugeSvg
            :dark="dark"
            gauge-type="horizontal"
            :pulse-score-current="text"
            :pulse-score-delta="scoreDelta"
            :pulse-average="`${formattedMinScore} - ${formattedMaxScore}`"
            @finished="handleChartFinished"
            class="ml-4"
          ></StabilityGaugeSvg>
        </div>
      </v-col>

      <v-col v-if="isHotspots" class="col-4">
        <div
          class="hyp-details-highlight-img-container mt-2"
          :class="{ 'mb-2': isHotspots }"
        >
          <v-img
            :src="src"
            :width="100"
            :height="100"
            alt="highlight image"
            class="hyp-details-highlight-img"
          ></v-img>
          <div
            v-show="text"
            class="hyp-details-highlight-text text-center"
            :class="{ 'hyp-details-highlight-text-lg': largeText }"
          >
            <span>{{ text }}</span>
          </div>
        </div>
      </v-col>

      <v-col v-if="isHotspots && triggeredBy" class="col-6">
        <div class="hyp-details-highlight-triggered text-center">
          Triggered by
          <p v-html="triggeredBy"></p>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { LABELS } from "@/common/constants/labels";

const EMITTED_ACTIONS = {
  FINISHED: "finished",
};

export default {
  name: "GridHighlight",
  components: {
    StabilityGaugeSvg: () =>
      import("@/components/grids/stability/StabilityGaugeSvg"),
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
  },
  props: {
    type: {
      type: String,
      validator: function (value) {
        return ["stability", "hotspots", "hotspotsCovid"].indexOf(value) !== -1;
      },
    },
    color: {
      type: String,
    },
    text: {
      type: [String, Number],
    },
    minScore: {
      type: Number,
    },
    maxScore: {
      type: Number,
    },
    scoreDelta: {
      type: Number,
    },
    isHistory: {
      type: Boolean,
      default: false,
    },
    trigger: {
      type: Array,
    },
    largeText: {
      type: Boolean,
      default: false,
    },
    dark: {
      type: Boolean,
      default: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      labels: LABELS,
    };
  },
  computed: {
    isStability() {
      return this.type === "stability";
    },
    isHotspots() {
      return this.type.indexOf("hotspots") > -1;
    },
    src() {
      return this.highlightColor
        ? require(`@/assets/images/miscellaneous/grid_highlight_${this.highlightColor}.png`)
        : null;
    },
    topText() {
      return "100";
    },
    bottomText() {
      return "0";
    },
    highlightColor() {
      if (this.color) {
        return this.color.toLowerCase();
      }
      return null;
    },
    triggeredBy() {
      if (this.trigger) {
        return this.trigger.join("<br />");
      }
      return null;
    },
    formattedMinScore() {
      if (this.minScore === undefined || this.minScore === null) {
        return null;
      }
      return Math.floor(this.minScore);
    },
    formattedMaxScore() {
      if (this.maxScore) {
        return Math.floor(this.maxScore);
      }
      return null;
    },
  },
  methods: {
    handleChartFinished() {
      this.$emit(EMITTED_ACTIONS.FINISHED, true);
    },
  },
};
</script>

<style scoped>
.details-heading {
  font-size: 20px;
}
.details-heading-dark {
  color: white;
  font-size: 16px;
}
.hyp-details-highlight-img-container {
  margin: auto;
  position: relative;
}
.hyp-details-highlight-img {
  margin: auto;
  position: relative;
}
.hyp-details-highlight-text {
  margin: auto;
  color: white;
  position: absolute;
  margin-left: 0;
  margin-top: -70px;
  font-size: 14px;
  width: 100%;
}
.hyp-details-highlight-text-lg {
  font-size: 40px;
  margin-top: -80px;
  width: 100%;
}
.hyp-details-highlight-bar-ball {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-bottom: 15px;
}
.hyp-details-highlight-bar-ball-level5 {
  background-color: blue;
}
.hyp-details-highlight-bar-ball-level4 {
  background-color: green;
}
.hyp-details-highlight-bar-ball-level3 {
  background-color: yellow;
}
.hyp-details-highlight-bar-ball-level2 {
  background-color: orange;
}
.hyp-details-highlight-bar-ball-level1 {
  background-color: red;
}
.hyp-details-highlight-bar-ball-level5-active {
  box-shadow: 0 0 10px 5px blue;
}
.hyp-details-highlight-bar-ball-level4-active {
  box-shadow: 0 0 10px 5px green;
}
.hyp-details-highlight-bar-ball-level3-active {
  box-shadow: 0 0 10px 5px yellow;
}
.hyp-details-highlight-bar-ball-level2-active {
  box-shadow: 0 0 10px 5px orange;
}
.hyp-details-highlight-bar-ball-level1-active {
  box-shadow: 0 0 10px 5px red;
}
.hyp-details-highlight-highest-text {
  margin-top: -6px;
}
.hyp-details-highlight-lowest-text {
  margin-top: 80px;
  text-align: right;
}
.hyp-details-highlight-triggered {
  margin-top: 30px;
}
.GaugeHolder {
  display: block;
  position: relative;
  height: 250px;
  min-width: 200px;
  margin-bottom: -100px;
}
.GaugeHolderSvg {
  display: block;
  position: relative;
  height: 160px;
  min-width: 200px;
}
.GaugeHolderSvgHorizontal {
  height: 70px;
}
.scoreHolder {
  text-align: center;
  height: 100%;
  width: 100%;
}
</style>
